import { useQuery } from '@tanstack/react-query';
import {
  createFileRoute,
  Outlet,
  redirect,
  useNavigate,
} from '@tanstack/react-router';
import { useEffect } from 'react';
import { ErrorState } from '@/components/feedback/error-state';
import { Loading } from '@/components/ui/loading';
import { onSessionRefused } from '@/lib/api';
import { currentStage, signOut } from '@/lib/auth';
import { adminQuery, queryClient } from '@/lib/query';
import { StaffContext } from '@/lib/staff-context';

/**
 * The walls (prototype spec §9.15): outside the app shell, no sidebar or top
 * bar, but behind the same sign-in. A wall never runs the idle sign-out (it is
 * meant to stay up); the server still ends the session at its own limit, and
 * then the wall goes back to sign-in.
 */
export const Route = createFileRoute('/wall')({
  beforeLoad: async () => {
    const stage = await currentStage();
    if (stage.stage !== 'done') throw redirect({ to: '/sign-in' });
  },
  component: WallLayout,
});

function WallLayout() {
  const navigate = useNavigate();
  const me = useQuery(adminQuery('session.me', {}));

  useEffect(
    () =>
      onSessionRefused(() => {
        void (async () => {
          await signOut();
          queryClient.clear();
          await navigate({ to: '/sign-in', search: { reason: 'expired' } });
        })();
      }),
    [navigate],
  );

  if (me.isPending) {
    return (
      <div className="grid min-h-dvh place-items-center">
        <Loading />
      </div>
    );
  }
  if (me.isError) {
    return (
      <div className="grid min-h-dvh place-items-center p-6">
        <ErrorState
          title="Could not load your account."
          reason={me.error.message}
          onRetry={() => me.refetch()}
        />
      </div>
    );
  }
  return (
    <StaffContext value={me.data}>
      <Outlet />
    </StaffContext>
  );
}
